// The Ranking widget on ?live=1: Ben's own ranking (`data/rankings.ts` computeRanking) run on the
// shared group (`live/group-players.ts`) — her league's file for the season picked.
//
// The one difference from the snapshot: with a Position chip on, the rows are minutes AT each
// position (Ben's Position rule, rules.md), so a player who played two of the chip's positions is
// listed once per position (Q-014: one dot per position). Each such row says which position it is
// ("at Full Back") where the snapshot shows the league — the readout already names the league —
// so a second row for her reads as an answer, not a bug.

import type { ProtoRankingEntry } from "@catbento/shared";
import { computeRanking } from "../data/rankings.js";
import { humanizePositionTemplate } from "../modules/bio-view.js";
import { LIVE_RANKING_RANGE_FILTERS } from "../modules/rankings-view.js";
import type { ActiveFilters, KitMetric, KitPlayer } from "../engines/stats-filter-kit.js";
import type { GroupPlayer } from "./group-players.js";

export function liveRanking(
  players: Record<string, GroupPlayer>,
  metric: KitMetric,
  seasonCode: string,
  useTotal: boolean,
  active: ActiveFilters,
  refPlayer: KitPlayer,
  byPosition: boolean,
): ProtoRankingEntry[] {
  const rank = (ps: Record<string, GroupPlayer>) =>
    computeRanking(ps, metric, seasonCode, useTotal, active, refPlayer, LIVE_RANKING_RANGE_FILTERS);
  if (!byPosition) return rank(players);
  // One ranking per position (each player at most once in each), labelled, then merged. The sort
  // is stable, so equal values keep Ben's order within a position.
  const byGroup = new Map<string, Record<string, GroupPlayer>>();
  for (const [key, p] of Object.entries(players)) {
    const group = p.positionTemplate ?? "";
    let ps = byGroup.get(group);
    if (!ps) byGroup.set(group, (ps = {}));
    ps[key] = p;
  }
  return [...byGroup]
    .flatMap(([group, ps]) => {
      // Her minutes at this position, for the "why twice" hover (group-players `whyTwice`).
      const minutes = new Map(Object.values(ps).map((p) => [p.playerId, p.stats.minutes ?? null] as const));
      return rank(ps).map((e) => ({
        ...e,
        competition: `at ${humanizePositionTemplate(group)}`,
        positionMinutes: minutes.get(e.playerId) ?? null,
      }));
    })
    .sort((a, b) => b.value - a.value);
}
